import { useQuery } from '@tanstack/react-query'

import type { CandidateStateLabels } from './candidateStateInsightsService'
import {
  calculateRetryDelay,
  shouldRetryQuery
} from '@percent/partner-dashboard/common/hooks/useInfiniteQuery/useInfiniteQuery'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import type { ProgramState } from '@percent/partner-dashboard/services/grants/grantsService.types'

const EMPTY: CandidateStateLabels = { idsByLabel: {}, labelById: {}, labelsByProgram: {} }

type ProgramStates = { name: string; states: ProgramState[] }

const isOffered = (state: ProgramState) => Boolean(state.displayKey) && state.displayMode !== 'hidden'

export const toCandidateStateLabels = (programs: ProgramStates[]): CandidateStateLabels => {
  const named = programs.flatMap(program => program.states).filter(state => Boolean(state.displayKey))
  const labelById: Record<string, string> = {}
  const idsByLabel: Record<string, string[]> = {}
  const labelsByProgram: Record<string, string[]> = {}

  named.forEach(state => {
    labelById[state.id] ??= state.displayKey
  })

  named.filter(isOffered).forEach(state => {
    const ids = idsByLabel[state.displayKey] ?? []

    idsByLabel[state.displayKey] = ids.includes(state.id) ? ids : [...ids, state.id]
  })

  programs.forEach(({ name, states }) => {
    labelsByProgram[name] = [
      ...new Set([...(labelsByProgram[name] ?? []), ...states.filter(isOffered).map(state => state.displayKey)])
    ]
  })

  return { idsByLabel, labelById, labelsByProgram }
}

/**
 * Every state of every programme the partner runs, keyed both ways: the candidates report
 * shows a state by its display name, while the API filters and groups on the state id. A
 * name a programme defines for several ids, or several programmes share, covers all of them.
 * Each programme's names are kept too, so the state picker can follow the programme filter.
 */
export function useCandidateStateLabels() {
  const { grantsService } = useServices()

  const { data, isLoading } = useQuery({
    queryKey: ['grantCandidateStateLabels'],
    queryFn: async () => {
      const programs = (await grantsService.getPrograms()).data.data
      const states = await Promise.all(
        programs.map(async program => ({
          name: program.name,
          states: (await grantsService.getProgramStates({ programId: program.id })).data.data.states
        }))
      )

      return toCandidateStateLabels(states)
    },
    staleTime: 1000 * 60 * 5,
    retry: (failureCount, error) => shouldRetryQuery(failureCount, error),
    retryDelay: attemptIndex => calculateRetryDelay(attemptIndex)
  })

  return { labels: data ?? EMPTY, isLoading }
}
